import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Loader2, Receipt, Check, FileText, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DateFieldPT from "@/components/DateFieldPT";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import MoneyInput from "@/components/MoneyInput";

const METHODS = [
  { v: "pix", l: "PIX" },
  { v: "transferencia", l: "Transferência bancária" },
  { v: "dinheiro", l: "Dinheiro" },
  { v: "cheque", l: "Cheque" },
  { v: "outro", l: "Outro" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * SocioPaymentsCard — balance panel + payments history + register-payment dialog
 * for a single socio. Fetches /finance/socios/:id/balance and /payments on mount.
 *
 * Props:
 *  - socio: {id, name}
 *  - onBalanceChange?: (balance) => void   (e.g. refresh the parent list)
 *  - initialBalance?: {earned, paid, balance}  (optional preload)
 */
export default function SocioPaymentsCard({ socio, onBalanceChange, initialBalance }) {
  const [balance, setBalance] = useState(initialBalance || null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      // Single call: statement returns balance + unified ledger (NF entries + payments)
      const r = await api.get(`/finance/socios/${socio.id}/statement`);
      const d = r.data || {};
      setBalance({ earned: d.earned, paid: d.paid, balance: d.balance });
      setEntries(d.entries || []);
      onBalanceChange?.({ earned: d.earned, paid: d.paid, balance: d.balance });
    } catch (e) {
      toast.error("Erro ao carregar extrato do sócio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (socio?.id) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socio?.id]);

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Cancelar este pagamento? O saldo pendente voltará a incluí-lo.")) return;
    try {
      await api.delete(`/finance/socios/${socio.id}/payments/${paymentId}`);
      toast.success("Pagamento cancelado");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar");
    }
  };

  if (loading && !balance) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando saldo...
      </div>
    );
  }

  const earned = balance?.earned || 0;
  const paid = balance?.paid || 0;
  const pending = balance?.balance || 0;
  const settled = pending <= 0.009;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="socio-payments-card">
      {/* Header — stacks on mobile so the button never squeezes the title */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
          <Wallet className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900 leading-tight">
            Pagamentos ao sócio
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Registre as transferências — o saldo é recalculado automaticamente.
          </p>
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => setDialogOpen(true)}
        className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white mb-4"
        data-testid="btn-register-payment"
      >
        <Plus className="w-4 h-4 mr-1.5" />
        Registrar pagamento
      </Button>

      {/* Balance stats — saldo pendente ocupa linha inteira no mobile (destaque) */}
      <div className="grid grid-cols-2 gap-2">
        <div
          className={`col-span-2 rounded-xl border p-3 ${
            settled ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"
          }`}
          data-testid="balance-pending"
        >
          <p
            className={`text-[10px] uppercase tracking-wider font-semibold ${
              settled ? "text-emerald-700" : "text-amber-800"
            }`}
          >
            {settled ? "Saldo zerado" : "Saldo pendente"}
          </p>
          <p
            className={`font-black text-xl sm:text-2xl mt-1 tabular-nums ${
              settled ? "text-emerald-700" : "text-amber-800"
            }`}
          >
            {settled ? (
              <span className="inline-flex items-center gap-1 text-lg">
                <Check className="w-5 h-5" /> Em dia
              </span>
            ) : (
              BRL(pending)
            )}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="balance-earned">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Devido (NFs)
          </p>
          <p className="font-bold text-base text-slate-900 mt-1 tabular-nums">{BRL(earned)}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3" data-testid="balance-paid">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Pago</p>
          <p className="font-bold text-base text-emerald-700 mt-1 tabular-nums">{BRL(paid)}</p>
        </div>
      </div>

      {/* Extrato unificado — entradas (NFs) + saídas (pagamentos) com saldo após */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Extrato ({entries.length})
          </h3>
          <p className="text-[10px] text-slate-400">
            + entradas (NFs) · − saídas (pagamentos)
          </p>
        </div>
        {entries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center">
            <Receipt className="w-7 h-7 mx-auto text-slate-300 mb-1.5" />
            <p className="text-sm text-slate-500">Nenhum movimento registrado ainda.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => {
              const isCredit = e.kind === "credit";
              return (
                <li
                  key={`${e.kind}-${e.ref_id}`}
                  data-testid={`entry-row-${e.kind}-${e.ref_id}`}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${
                    isCredit
                      ? "border-emerald-100 bg-emerald-50/40"
                      : "border-amber-100 bg-amber-50/40"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isCredit ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {isCredit ? (
                      <ArrowUpRight className="w-4 h-4" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p
                        className={`font-bold tabular-nums text-sm ${
                          isCredit ? "text-emerald-700" : "text-amber-800"
                        }`}
                      >
                        {isCredit ? "+" : "−"}
                        {BRL(e.amount)}
                      </p>
                      <p className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">
                        {formatDateBR(e.date)}
                      </p>
                    </div>
                    <p className="text-xs text-slate-700 font-medium mt-0.5 break-words">
                      {isCredit ? (
                        <>
                          <FileText className="inline w-3 h-3 mr-1 -mt-0.5 text-emerald-700" />
                          {e.label}
                        </>
                      ) : (
                        e.label
                      )}
                    </p>
                    {isCredit && e.school_name && (
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                        {e.school_name}
                        {e.tier != null ? ` · Faixa ${e.tier} · ${e.pct}%` : ""}
                      </p>
                    )}
                    {!isCredit && e.note && (
                      <p className="text-[11px] text-slate-500 mt-0.5 break-words">{e.note}</p>
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      Saldo após: <strong className="text-slate-700">{BRL(e.balance_after)}</strong>
                    </p>
                  </div>
                  {!isCredit && (
                    <button
                      type="button"
                      onClick={() => deletePayment(e.ref_id)}
                      className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Cancelar pagamento"
                      data-testid={`btn-delete-payment-${e.ref_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <RegisterPaymentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        socio={socio}
        suggested={Math.max(0, pending)}
        onSaved={refresh}
      />
    </div>
  );
}

function RegisterPaymentDialog({ open, onOpenChange, socio, suggested, onSaved }) {
  const [notas, setNotas] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  // Per-nota custom amount (partial payment). Keyed by budget_id. Empty
  // string ou null → paga o remaining completo da nota.
  const [customAmount, setCustomAmount] = useState(() => ({}));
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState("pix");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Load eligible notas every time the dialog opens.
  useEffect(() => {
    if (!open || !socio?.id) return;
    setLoading(true);
    setSelected(new Set());
    setCustomAmount({});
    (async () => {
      try {
        const r = await api.get(`/finance/socios/${socio.id}/available-notas`);
        const list = Array.isArray(r.data) ? r.data : [];
        setNotas(list);
        setSelected(new Set(list.filter((n) => n.is_adjustment).map((n) => n.budget_id)));
      } catch {
        setNotas([]);
      } finally {
        setLoading(false);
      }
    })();
    setDate(todayIso());
    setMethod("pix");
    setNote("");
    setSaving(false);
  }, [open, socio?.id]);

  const toggleOne = (bid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(bid)) {
        next.delete(bid);
        setCustomAmount((prevCA) => {
          const nextCA = { ...prevCA };
          delete nextCA[bid];
          return nextCA;
        });
      } else {
        next.add(bid);
      }
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === notas.length) {
      setSelected(new Set());
      setCustomAmount({});
    } else {
      setSelected(new Set(notas.map((n) => n.budget_id)));
    }
  };

  // Effective amount for a nota — custom partial OR full remaining.
  const effectiveAmount = (n) => {
    if (n.is_adjustment) return n.remaining;
    const raw = customAmount[n.budget_id];
    if (raw == null || raw === "") return n.remaining;
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(parsed) || parsed <= 0) return 0;
    return Math.min(parsed, n.remaining);
  };
  const total = useMemo(
    () => notas.filter((n) => selected.has(n.budget_id)).reduce((s, n) => s + effectiveAmount(n), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notas, selected, customAmount],
  );

  const toggleExclude = async (nota) => {
    if (!window.confirm(
      `Marcar a nota #${nota.budget_number} (${nota.school_name}) como FAVOR?\nEla sai da divisão de sócios e não entra mais no cálculo.`,
    )) return;
    try {
      await api.post(`/budgets/${nota.budget_id}/toggle-exclude-socios`);
      toast.success(`Nota #${nota.budget_number} marcada como favor`);
      // Reload the list — the excluded nota disappears.
      const r = await api.get(`/finance/socios/${socio.id}/available-notas`);
      setNotas(Array.isArray(r.data) ? r.data : []);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(nota.budget_id);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível alterar");
    }
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Selecione pelo menos uma nota pra pagar.");
      return;
    }
    // Build the per-budget partial amount map. Only include notas where
    // the user actually typed a custom amount (!= remaining).
    const amountsMap = {};
    for (const n of notas) {
      if (!selected.has(n.budget_id) || n.is_adjustment) continue;
      const eff = effectiveAmount(n);
      if (eff <= 0) {
        toast.error(`Nota #${n.budget_number}: valor precisa ser maior que zero.`);
        return;
      }
      if (Math.abs(eff - n.remaining) > 0.009) {
        amountsMap[n.budget_id] = Number(eff.toFixed(2));
      }
    }
    if (total <= 0) {
      toast.error("O total (notas − ajustes) precisa ser maior que zero. Selecione mais notas.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/finance/socios/${socio.id}/payments`, {
        payment_date: date,
        method,
        note: note.trim(),
        budget_ids: Array.from(selected),
        budget_amounts: Object.keys(amountsMap).length ? amountsMap : undefined,
      });
      toast.success(`Pagamento de ${BRL(total)} registrado`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar pagamento");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = selected.size > 0 && selected.size === notas.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="register-payment-dialog"
        className="max-w-xl max-h-[85vh] flex flex-col p-0 overflow-hidden"
      >
        <DialogHeader className="p-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" />
            Pagar sócio: {socio?.name}
          </DialogTitle>
          <DialogDescription className="text-[12px] mt-1">
            Marque as notas pagas pelo cliente que você está repassando agora.
            O valor é a parte deste sócio em cada nota (já descontados impostos e despesas).
          </DialogDescription>
        </DialogHeader>

        {/* Lista rolável de notas */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Carregando notas pagas…
            </div>
          ) : notas.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">
              <Receipt className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              Nenhuma nota disponível pra repasse. Só aparecem aqui as notas
              marcadas como <strong>Nota paga</strong> cujo valor pra {socio?.name} ainda
              não foi repassado.
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={toggleAll}
                className="flex items-center gap-2 text-[11px] font-bold text-emerald-700 hover:text-emerald-900"
                data-testid="btn-toggle-all-notas"
              >
                <span
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    allSelected ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"
                  }`}
                >
                  {allSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                </span>
                {allSelected ? "Desmarcar todas" : "Selecionar todas"} ({notas.length})
              </button>

              {notas.map((n) => {
                const checked = selected.has(n.budget_id);
                const partial = n.already_paid > 0;
                const custom = customAmount[n.budget_id];
                const eff = effectiveAmount(n);
                const isCustom = checked && custom != null && custom !== "" && Math.abs(eff - n.remaining) > 0.009;
                return (
                  <div
                    key={n.budget_id}
                    className={`rounded-xl border p-3 flex items-start gap-3 transition-colors ${
                      checked
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`nota-row-${n.budget_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleOne(n.budget_id)}
                      className={`w-5 h-5 mt-0.5 rounded border-2 shrink-0 flex items-center justify-center transition ${
                        checked
                          ? "bg-emerald-600 border-emerald-600 text-white"
                          : "border-slate-300 hover:border-emerald-400"
                      }`}
                      data-testid={`check-nota-${n.budget_number}`}
                    >
                      {checked && <Check className="w-3 h-3" strokeWidth={3} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-heading font-black text-[13px] text-slate-900">
                          #{n.budget_number} — {n.school_name}
                        </span>
                        <span className={`font-bold text-sm ${n.is_adjustment ? "text-rose-700" : isCustom ? "text-amber-700" : "text-emerald-700"}`}>
                          {n.is_adjustment ? `− ${BRL(Math.abs(eff))}` : BRL(eff)}
                          {isCustom && <span className="font-normal text-[10px] text-slate-500"> / {BRL(n.remaining)}</span>}
                        </span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-600">
                        {n.nf_number && <span>NF {n.nf_number}</span>}
                        {n.nf_date && <span>· {formatDateBR(n.nf_date)}</span>}
                        <span>· {n.pct}% F{n.tier}</span>
                        {n.is_adjustment && (
                          <span className="text-rose-700 font-semibold" data-testid={`nota-adjustment-${n.budget_number}`}>· Ajuste de correção: recebeu {BRL(n.already_paid)}, devido {BRL(n.full_value)} (desconto)</span>
                        )}
                        {partial && !n.is_adjustment && (
                          <span className="text-amber-700">· já pago {BRL(n.already_paid)} de {BRL(n.full_value)}</span>
                        )}
                      </div>

                      {checked && !n.is_adjustment && (
                        <div className="mt-2 flex items-center gap-1.5">
                          <label className="text-[10.5px] font-bold text-slate-600">Pagar:</label>
                          <MoneyInput
                            value={custom != null && custom !== "" ? custom : ""}
                            onChange={(v) =>
                              setCustomAmount((prev) => ({ ...prev, [n.budget_id]: v }))
                            }
                            placeholder={BRL(n.remaining)}
                            data-testid={`amount-nota-${n.budget_number}`}
                            className="h-7 text-xs max-w-[130px]"
                          />
                          {isCustom && (
                            <button
                              type="button"
                              onClick={() =>
                                setCustomAmount((prev) => {
                                  const next = { ...prev };
                                  delete next[n.budget_id];
                                  return next;
                                })
                              }
                              className="text-[10px] text-indigo-600 hover:text-indigo-800 font-semibold whitespace-nowrap"
                              data-testid={`amount-reset-${n.budget_number}`}
                            >
                              pagar tudo
                            </button>
                          )}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => toggleExclude(n)}
                        data-testid={`btn-exclude-${n.budget_id}`}
                        className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5"
                      >
                        🎁 Fora da divisão (favor)
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Rodapé com total + metadata + ações */}
        <div className="border-t bg-slate-50/70 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-slate-600">
              {selected.size} nota{selected.size !== 1 ? "s" : ""} selecionada{selected.size !== 1 ? "s" : ""}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-bold text-slate-500">Total a pagar</div>
              <div className="font-heading text-xl font-black text-emerald-700" data-testid="payment-total">
                {BRL(total)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-slate-700 mb-1">Data</label>
              <DateFieldPT value={date} onChange={setDate} testid="payment-date" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-700 mb-1">Método</label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payment-method" className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Observação (opcional) — ex.: PIX dia 15/10"
            data-testid="payment-note"
            className="resize-none text-xs"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="flex-1"
              data-testid="btn-cancel-payment"
            >
              Cancelar
            </Button>
            <Button
              onClick={submit}
              disabled={saving || selected.size === 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-save-payment"
            >
              {saving ? "Salvando..." : `Registrar ${BRL(total)}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
